/**
 * @fileoverview Connect recorded engagement instructions, autofill suggestions, and managed draft preparation. The component owns editable form state and preview/polling; `intakeApi.js` owns requests and the backend owns protected terms and accepted draft state. Suggestions fill eligible blanks without silently replacing staff edits. Draft preparation, document creation, and client delivery remain separate business actions.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiUrl } from '../Documents/archiveReviewUtils';
import AppIcon from '../shared/AppIcon';
import AppModal from '../shared/AppModal';
import {
  approveIntakeEngagementDraft,
  fetchIntakeEngagementDraft,
  generateIntakeEngagementDraft,
  requestIntakeEngagementAutofill,
} from './intakeApi';
import './EngagementDraftPreview.css';

const ACTIVE_STATUSES = new Set(['queued', 'generating']);
const AUTOFILL_ACTIVE_STATUSES = new Set(['queued', 'running']);



function statusLabel(status) {
  if (status === 'queued' || status === 'generating') return 'Preparing draft';
  if (status === 'needs_input') return 'Needs information';
  if (status === 'ready') return 'Draft ready';
  if (status === 'approved_for_finalization') return 'Approved final draft';
  if (status === 'abandoned') return 'Not proceeding';
  if (status === 'failed') return 'Draft needs attention';
  return 'Engagement package';
}



function lines(items, objectKey = '') {
  return (items || [])
    .map((item) => objectKey && item && typeof item === 'object' ? item[objectKey] : item)
    .filter(Boolean)
    .join('\n');
}



function lineItems(value, objectKey = '') {
  return String(value || '')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => objectKey ? { [objectKey]: item } : item);
}



function formFromDraft(draft) {
  const instruction = draft?.instruction || {};
  const terms = instruction.protected_terms || {};
  return {
    clients: lines(instruction.clients, 'name'),
    adverseParties: lines(instruction.adverse_parties, 'name'),
    claimTypes: lines(instruction.claim_types),
    claimDescription: instruction.claim_description || '',
    clientState: instruction.client_state || '',
    feeStructure: terms.fee_structure || '',
    localCounsel: terms.local_counsel || '',
    otherTerms: terms.other_terms || '',
    releaseSigned: Boolean(terms.release_signed),
    contingentFeeIncluded: Boolean(terms.contingent_fee_included),
    texasArbitrationNotice: Boolean(terms.texas_arb_notice),
  };
}

/**
 * Apply recorded suggestions only to blank eligible fields. Preserve staff-entered values; the result reports which fields changed so the editor can distinguish applied suggestions from accepted saved instructions.
 */
function applyBlankAutofillFields(current, autofill) {
  const fields = autofill?.suggestions?.fields || {};
  const next = { ...current };
  const applied = [];
  const setBlankText =

  (formKey, suggestionKey) => {
    const value = fields[suggestionKey]?.value;
    if (!String(next[formKey] || '').trim() && typeof value === 'string' && value.trim()) {
      next[formKey] = value.trim();
      applied.push(formKey);
    }
  };
  setBlankText('claimDescription', 'claim_description');
  setBlankText('feeStructure', 'fee_structure');
  setBlankText('localCounsel', 'local_counsel');
  setBlankText('otherTerms', 'other_terms');
  if (!next.contingentFeeIncluded && fields.contingent_fee_included?.value === true) {
    next.contingentFeeIncluded = true;
    applied.push('contingentFeeIncluded');
  }
  if (!next.texasArbitrationNotice && fields.texas_arb_notice?.value === true) {
    next.texasArbitrationNotice = true;
    applied.push('texasArbitrationNotice');
  }
  return { form: next, applied };
}



export default function EngagementDraftPreview({ draft, onRefresh, documentActionLabel = 'Open in Documents' }) {
  const [currentDraft, setCurrentDraft] = useState(draft);
  const [form, setForm] = useState(() => formFromDraft(draft));
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saving, setSaving] = useState('');
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const [polling, setPolling] = useState(false);
  const [autofillPolling, setAutofillPolling] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [suggestedFields, setSuggestedFields] = useState(() => new Set());
  const autofillRequestedRef = useRef('');
  const autofillAppliedRef = useRef('');

  useEffect(() => {
    setCurrentDraft(draft);
    const baseForm = formFromDraft(draft);
    if (draft?.autofill?.status === 'ready') {
      const result = applyBlankAutofillFields(baseForm, draft.autofill);
      setForm(result.form);
      setDirty(result.applied.length > 0);
      setSuggestedFields(new Set(result.applied));
      autofillAppliedRef.current = draft.autofill.completed_at
        || draft.autofill.model?.response_id
        || `${draft.id}:ready`;
    } else {
      setForm(baseForm);
      setDirty(false);
      setSuggestedFields(new Set());
    }
  }, [draft]);

  useEffect(

  () => {
    const autofill = currentDraft?.autofill;
    if (currentDraft?.status !== 'needs_input' || autofill?.status !== 'not_started') return;
    const requestKey = `${currentDraft.id}:${autofill.version || 'v1'}`;
    if (autofillRequestedRef.current === requestKey) return;
    autofillRequestedRef.current = requestKey;
    let cancelled = false;
    requestIntakeEngagementAutofill(currentDraft.lead_id)
      .then((response) => {
        if (cancelled) return;
        const nextDraft = response.data?.engagement_draft;
        if (nextDraft) {
          setCurrentDraft(nextDraft);
          setAutofillPolling(AUTOFILL_ACTIVE_STATUSES.has(nextDraft.autofill?.status));
        }
      })
      .catch(() => {
        if (!cancelled) setAutofillPolling(false);
      });
    return () => { cancelled = true; };
  }, [currentDraft?.autofill, currentDraft?.id, currentDraft?.lead_id, currentDraft?.status]);

  useEffect(() => {
    if (AUTOFILL_ACTIVE_STATUSES.has(currentDraft?.autofill?.status)) setAutofillPolling(true);
  }, [currentDraft?.autofill?.status]);

  useEffect(

  () => {
    const autofill = currentDraft?.autofill;
    if (autofill?.status !== 'ready') return;
    const applicationKey = autofill.completed_at || autofill.model?.response_id || `${currentDraft.id}:ready`;
    if (autofillAppliedRef.current === applicationKey) return;
    autofillAppliedRef.current = applicationKey;
    setForm((current) => {
      const result = applyBlankAutofillFields(current, autofill);
      if (result.applied.length) {
        setSuggestedFields(new Set(result.applied));
        setDirty(true);
      }
      return result.form;
    });
  }, [currentDraft?.autofill, currentDraft?.id]);

  useEffect(() => {
    if (!polling || !currentDraft?.lead_id) return undefined;
    const timer = window.setInterval(

    async () => {
      try {
        const response = await fetchIntakeEngagementDraft(currentDraft.lead_id);
        const nextDraft = response.data?.engagement_draft;
        if (!nextDraft) return;
        setCurrentDraft(nextDraft);
        if (!ACTIVE_STATUSES.has(nextDraft.status)) {
          setPolling(false);
          setForm(formFromDraft(nextDraft));
          setFeedback(nextDraft.status === 'ready' ? 'Draft generated and ready for review.' : 'Draft preparation finished.');
          await onRefresh?.();
        }
      } catch (refreshError) {
        setPolling(false);
        setError(refreshError?.response?.data?.error || 'Could not refresh the engagement draft.');
      }
    }, 1500);
    return () => window.clearInterval(timer);
  }, [currentDraft?.lead_id, onRefresh, polling]);

  useEffect(() => {
    if (!autofillPolling || !currentDraft?.lead_id) return undefined;
    const timer = window.setInterval(

    async () => {
      try {
        const response = await fetchIntakeEngagementDraft(currentDraft.lead_id);
        const nextDraft = response.data?.engagement_draft;
        if (!nextDraft) return;
        setCurrentDraft(nextDraft);
        if (!AUTOFILL_ACTIVE_STATUSES.has(nextDraft.autofill?.status)) {
          setAutofillPolling(false);
        }
      } catch (refreshError) {
        setAutofillPolling(false);
      }
    }, 1500);
    return () => window.clearInterval(timer);
  }, [autofillPolling, currentDraft?.lead_id]);

  const manifest = currentDraft?.input_manifest || {};
  const missing = Array.isArray(currentDraft?.missing_fields) ? currentDraft.missing_fields.filter(Boolean) : [];
  const unresolvedMissing = missing.filter(

  (item) => {
    if (item === 'Client name') return !form.clients.trim();
    if (item === 'Adverse party') return !form.adverseParties.trim();
    if (item === 'Representation scope / claim type') return !form.claimTypes.trim();
    if (item === 'Client state') return !form.clientState.trim();
    if (item === 'Attorney-approved fee structure') return !form.feeStructure;
    if (item === 'Approved local-counsel instruction') return !form.localCounsel.trim();
    return true;
  });
  const previewUrl = currentDraft?.preview_url ? apiUrl(currentDraft.preview_url) : '';
  const generating = ACTIVE_STATUSES.has(currentDraft?.status);
  const approved = currentDraft?.status === 'approved_for_finalization';
  const autofill = currentDraft?.autofill || {};
  const autofillSources = Array.isArray(autofill.sources) ? autofill.sources : [];
  const feeOptions = manifest.fee_structure_options || [];
  const templateLabel = manifest.template?.label || 'Engagement agreement';
  const canSubmit = Boolean(
    form.clients.trim()
    && form.adverseParties.trim()
    && form.claimTypes.trim()
    && form.clientState.trim()
    && form.feeStructure
    && (!manifest.local_counsel_required || form.localCounsel.trim())
  );
  const actionLabel = currentDraft?.document_id ? 'Regenerate draft' : 'Generate draft';
  const revisionLabel = useMemo(() => {
    const revisionId = manifest.template?.revision_id || '';
    return revisionId ? `Template revision ${revisionId.slice(0, 8)}` : 'Published template required';
  }, [manifest.template?.revision_id]);

  if (!currentDraft) return null;

  const updateForm =

  (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setSuggestedFields(

    (current) => {
      if (!current.has(key)) return current;
      const next = new Set(current);
      next.delete(key);
      return next;
    });
    setDirty(true);
    setFeedback('');
    setError('');
  };

  const generateDraft =

  async (event) => {
    event.preventDefault();
    if (!canSubmit || generating) return;
    setSaving('generate');
    setFeedback('');
    setError('');
    try {
      const response = await generateIntakeEngagementDraft(currentDraft.lead_id, {
        manifest_version: manifest.version,
        facts: {
          clients: lineItems(form.clients, 'name'),
          adverse_parties: lineItems(form.adverseParties, 'name'),
          claim_types: lineItems(form.claimTypes),
          claim_description: form.claimDescription,
          client_state: form.clientState,
        },
        protected_terms: {
          fee_structure: form.feeStructure,
          local_counsel: form.localCounsel,
          other_terms: form.otherTerms,
          release_signed: form.releaseSigned,
          contingent_fee_included: form.contingentFeeIncluded,
          texas_arb_notice: form.texasArbitrationNotice,
        },
      });
      const nextDraft = response.data?.engagement_draft || currentDraft;
      setCurrentDraft(nextDraft);
      setDirty(false);
      setFeedback('Preparing the draft from the approved template.');
      setPolling(ACTIVE_STATUSES.has(nextDraft.status));
      await onRefresh?.();
    } catch (saveError) {
      const nextDraft = saveError?.response?.data?.engagement_draft;
      if (nextDraft) setCurrentDraft(nextDraft);
      setError(saveError?.response?.data?.error || 'Could not generate the engagement draft.');
    } finally {
      setSaving('');
    }
  };

  const approveDraft =

  async () => {
    setSaving('approve');
    setFeedback('');
    setError('');
    try {
      const response = await approveIntakeEngagementDraft(currentDraft.lead_id);
      const nextDraft = response.data?.engagement_draft || currentDraft;
      setCurrentDraft(nextDraft);
      setFeedback('Final draft approved. It is ready to download and send.');
      await onRefresh?.();
    } catch (approvalError) {
      setError(approvalError?.response?.data?.error || 'Could not approve the engagement draft.');
    } finally {
      setSaving('');
    }
  };

  return (
    <section className="engagement-draft" aria-label="Engagement package">
      <header className="engagement-draft__header">
        <div>
          <span>{templateLabel}</span>
          <h2>Prepare agreement</h2>
          <small>{revisionLabel}</small>
        </div>
        <strong className={`engagement-draft__status engagement-draft__status--${currentDraft.status || 'unknown'}`}>
          <AppIcon icon={generating ? 'pi pi-spin pi-spinner' : currentDraft.status === 'failed' ? 'pi pi-exclamation-circle' : approved ? 'pi pi-check-circle' : 'pi pi-file-edit'} aria-hidden="true" />
          {statusLabel(currentDraft.status)}
        </strong>
      </header>

      <div className="engagement-draft__workspace">
        <form className="engagement-draft__form" onSubmit={generateDraft}>
          {AUTOFILL_ACTIVE_STATUSES.has(autofill.status) && <div className="engagement-draft__autofill engagement-draft__autofill--working" role="status"><AppIcon icon="pi pi-spin pi-spinner" aria-hidden="true" /><span>Finding similar agreements</span></div>}
          {autofill.status === 'ready' && autofillSources.length > 0 && <details className="engagement-draft__autofill">
            <summary><AppIcon icon="pi pi-sparkles" aria-hidden="true" /><span>AI suggested from {autofillSources.length} similar {autofillSources.length === 1 ? 'agreement' : 'agreements'}</span><AppIcon icon="pi pi-chevron-down engagement-draft__autofill-chevron" aria-hidden="true" /></summary>
            <div className="engagement-draft__autofill-sources">
              {autofillSources.map((source) => <div key={source.revision_id}><strong>{source.filename || 'Engagement agreement'}</strong><small>{[source.matter_number, ...(source.reasons || [])].filter(Boolean).join(' · ')}</small></div>)}
            </div>
          </details>}
          {autofill.status === 'insufficient_history' && <div className="engagement-draft__autofill engagement-draft__autofill--quiet" role="status"><AppIcon icon="pi pi-info-circle" aria-hidden="true" /><span>No reliable similar agreement found</span></div>}
          <fieldset disabled={generating || Boolean(saving)}>
            <legend>Case information</legend>
            <label><span>Client name</span><textarea rows="2" required value={form.clients} onChange={(event) => updateForm('clients', event.target.value)} /></label>
            <label><span>Adverse party</span><textarea rows="2" required value={form.adverseParties} onChange={(event) => updateForm('adverseParties', event.target.value)} /></label>
            <div className="engagement-draft__form-row">
              <label><span>Representation scope</span><textarea rows="2" required value={form.claimTypes} onChange={(event) => updateForm('claimTypes', event.target.value)} /></label>
              <label><span>Client state</span><input required value={form.clientState} onChange={(event) => updateForm('clientState', event.target.value)} /></label>
            </div>
            <label className={suggestedFields.has('claimDescription') ? 'engagement-draft__suggested' : ''}><span>Claim description{suggestedFields.has('claimDescription') && <AppIcon icon="pi pi-sparkles" aria-hidden="true" />}</span><textarea rows="3" value={form.claimDescription} onChange={(event) => updateForm('claimDescription', event.target.value)} /></label>
          </fieldset>

          <fieldset disabled={generating || Boolean(saving)}>
            <legend>Attorney decisions</legend>
            <label className={suggestedFields.has('feeStructure') ? 'engagement-draft__suggested' : ''}>
              <span>Fee structure{suggestedFields.has('feeStructure') && <AppIcon icon="pi pi-sparkles" aria-hidden="true" />}</span>
              <select required value={form.feeStructure} onChange={(event) => updateForm('feeStructure', event.target.value)}>
                <option value="">Select approved language</option>
                {feeOptions.map((option) => <option value={option} key={option}>{option}</option>)}
              </select>
            </label>
            {manifest.local_counsel_required && <label className={suggestedFields.has('localCounsel') ? 'engagement-draft__suggested' : ''}><span>Local-counsel instruction{suggestedFields.has('localCounsel') && <AppIcon icon="pi pi-sparkles" aria-hidden="true" />}</span><textarea rows="3" required value={form.localCounsel} onChange={(event) => updateForm('localCounsel', event.target.value)} /></label>}
            <div className="engagement-draft__checks">
              <label className={suggestedFields.has('contingentFeeIncluded') ? 'engagement-draft__suggested' : ''}><input type="checkbox" checked={form.contingentFeeIncluded} onChange={(event) => updateForm('contingentFeeIncluded', event.target.checked)} /><span>Include contingent-fee provision{suggestedFields.has('contingentFeeIncluded') && <AppIcon icon="pi pi-sparkles" aria-hidden="true" />}</span></label>
              <label><input type="checkbox" checked={form.releaseSigned} onChange={(event) => updateForm('releaseSigned', event.target.checked)} /><span>Client has signed a release</span></label>
              {manifest.show_texas_arbitration_notice && <label className={suggestedFields.has('texasArbitrationNotice') ? 'engagement-draft__suggested' : ''}><input type="checkbox" checked={form.texasArbitrationNotice} onChange={(event) => updateForm('texasArbitrationNotice', event.target.checked)} /><span>Include Texas arbitration notice{suggestedFields.has('texasArbitrationNotice') && <AppIcon icon="pi pi-sparkles" aria-hidden="true" />}</span></label>}
            </div>
            <label className={suggestedFields.has('otherTerms') ? 'engagement-draft__suggested' : ''}><span>Additional terms{suggestedFields.has('otherTerms') && <AppIcon icon="pi pi-sparkles" aria-hidden="true" />}</span><textarea rows="3" value={form.otherTerms} onChange={(event) => updateForm('otherTerms', event.target.value)} /></label>
          </fieldset>

          {unresolvedMissing.length > 0 && !generating && <div className="engagement-draft__needs" role="status"><strong>Required before generation</strong><ul>{unresolvedMissing.map((item) => <li key={item}>{item}</li>)}</ul></div>}
          {error && <div className="intake-inline-alert" role="alert"><span>{error}</span></div>}
          {feedback && <div className="engagement-draft__feedback" role="status"><AppIcon icon="pi pi-check-circle" aria-hidden="true" /><span>{feedback}</span></div>}

          <div className="engagement-draft__primary-actions">
            <button type="submit" className="intake-button intake-button--primary" disabled={!canSubmit || generating || Boolean(saving)}><AppIcon icon={saving === 'generate' || generating ? 'pi pi-spin pi-spinner' : currentDraft.document_id ? 'pi pi-refresh' : 'pi pi-file-edit'} aria-hidden="true" /><span>{saving === 'generate' || generating ? 'Preparing draft' : actionLabel}</span></button>
            {dirty && currentDraft.document_id && <small>Regenerating creates a new draft revision.</small>}
          </div>
        </form>

        <div className="engagement-draft__document">
          {currentDraft.generation_error && <div className="intake-inline-alert" role="alert"><span>{currentDraft.generation_error}</span></div>}
          {previewUrl ? <iframe className="engagement-draft__preview" src={previewUrl} title="Engagement draft preview" /> : <div className="engagement-draft__empty"><AppIcon icon={generating ? 'pi pi-spin pi-spinner' : 'pi pi-file'} aria-hidden="true" /><span>{generating ? 'Assembling the approved template from the reviewed answers.' : 'Complete the required fields to generate the first draft.'}</span></div>}

          <div className="engagement-draft__actions">
            {previewUrl && <button type="button" className="intake-button" onClick={() => setPreviewOpen(true)}><AppIcon icon="pi pi-eye" aria-hidden="true" /><span>Larger preview</span></button>}
            {currentDraft.document_id && <Link className="intake-button" to={`/documents/${encodeURIComponent(currentDraft.document_id)}`}><AppIcon icon="pi pi-external-link" aria-hidden="true" /><span>{documentActionLabel}</span></Link>}
            {currentDraft.can_approve && !dirty && <button type="button" className="intake-button intake-button--primary" disabled={Boolean(saving)} onClick={approveDraft}><AppIcon icon={saving === 'approve' ? 'pi pi-spin pi-spinner' : 'pi pi-check'} aria-hidden="true" /><span>{saving === 'approve' ? 'Approving' : 'Approve final'}</span></button>}
            {approved && currentDraft.download_url && <a className="intake-button intake-button--primary" href={apiUrl(currentDraft.download_url)}><AppIcon icon="pi pi-download" aria-hidden="true" /><span>Download final</span></a>}
          </div>
        </div>
      </div>

      <AppModal visible={previewOpen} title="Engagement Draft" description="Attorney review draft — not approved for client delivery." onClose={() => setPreviewOpen(false)} width="min(1120px, 96vw)" className="engagement-draft-modal"><iframe className="engagement-draft__modal-preview" src={previewUrl} title="Expanded engagement draft preview" /></AppModal>
    </section>
  );
}
